import { Badge, Box, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';

import { AreaChart, type AreaData } from '@revelio/replots';

import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { RoleSkillTimepoint } from '../hooks/useRoleSkillComposition';
import type { SkillTrend } from '../hooks/useSkillGap';
import type { UpliftMode } from '../IndividualPotentialPage';

interface MissingSkillCardProps {
  skillName: string;
  roleShare: number;
  upliftPercent: number | null;
  upliftDollar: number | null;
  trend: SkillTrend;
  trendChangePercent: number;
  timeseries: ReadonlyArray<RoleSkillTimepoint>;
  targetSalaryWithSkill: number | null;
  userSalary: number;
  upliftMode: UpliftMode;
  isLowerPriority?: boolean;
}

const TREND_CONFIG: Record<
  SkillTrend,
  {
    label: string;
    colorScheme: string;
    arrow: string;
    tooltip: string;
    color: string;
  }
> = {
  emerging: {
    label: 'Growing',
    colorScheme: 'green',
    arrow: '\u2191',
    tooltip:
      'Demand for this skill is growing — its share among role holders has increased >15% over the observed period',
    color: '#38A169',
  },
  stable: {
    label: 'Stable',
    colorScheme: 'gray',
    arrow: '\u2192',
    tooltip:
      'Demand for this skill is steady — its share among role holders has remained consistent over the observed period',
    color: '#A0AEC0',
  },
  commoditized: {
    label: 'Declining',
    colorScheme: 'orange',
    arrow: '\u2193',
    tooltip:
      'Demand for this skill is declining — its share among role holders has dropped >10% over the observed period',
    color: '#DD6B20',
  },
};

export const MissingSkillCard = ({
  skillName,
  roleShare,
  upliftPercent,
  upliftDollar,
  trend,
  trendChangePercent,
  timeseries,
  targetSalaryWithSkill,
  upliftMode,
  isLowerPriority = false,
}: MissingSkillCardProps) => {
  const salaryFormatter = useCurrencyFormatter();
  const trendInfo = TREND_CONFIG[trend];

  const hasUpliftData = upliftPercent !== null && upliftDollar !== null;
  const isPositiveUplift = hasUpliftData && (upliftDollar ?? 0) > 0;

  const sparklineData: AreaData[] = useMemo(() => {
    if (timeseries.length === 0) return [];
    return [
      {
        label: skillName,
        values: timeseries
          .filter((point) => point.date !== '')
          .map((point) => ({
            x: `${point.date}-01`,
            value: point.share,
          })),
      },
    ];
  }, [timeseries, skillName]);

  const formattedTrendChange = useMemo(() => {
    if (trendChangePercent === 0) return '0%';
    const sign = trendChangePercent > 0 ? '+' : '';
    return `${sign}${trendChangePercent.toFixed(1)}%`;
  }, [trendChangePercent]);

  return (
    <Box
      bg={isLowerPriority ? 'white' : 'gray.50'}
      borderRadius="md"
      p={4}
      border="1px solid"
      borderColor={isLowerPriority ? 'gray.100' : 'gray.200'}
      opacity={isLowerPriority ? 0.75 : 1}
    >
      <VStack align="stretch" spacing={3}>
        {/* Header: skill name + trend badge */}
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            {skillName}
          </Text>
          <Tooltip label={trendInfo.tooltip} fontSize="xs" hasArrow>
            <Badge
              colorScheme={trendInfo.colorScheme}
              fontSize="xs"
              px={2}
              borderRadius="full"
              cursor="help"
            >
              {trendInfo.arrow} {trendInfo.label}
            </Badge>
          </Tooltip>
        </HStack>

        {/* Demand trend sparkline + YoY change */}
        {sparklineData.length > 0 && (
          <HStack spacing={2} align="center">
            <Box flex="1" h="32px">
              <AreaChart
                data={sparklineData}
                colors={[trendInfo.color]}
                showXAxis={false}
                showYAxis={false}
                showGradients
                showNoData={false}
              />
            </Box>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color={`${trendInfo.colorScheme}.600`}
              whiteSpace="nowrap"
            >
              {formattedTrendChange}
            </Text>
          </HStack>
        )}

        {/* Role prevalence */}
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.500">
            Role prevalence
          </Text>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            {roleShare.toFixed(1)}%
          </Text>
        </HStack>

        {/* Uplift bar */}
        {hasUpliftData && (
          <Box>
            <Box h="6px" bg="gray.200" borderRadius="full" overflow="hidden">
              <Box
                h="100%"
                bg={isPositiveUplift ? 'green.400' : 'red.400'}
                borderRadius="full"
                w={`${Math.min(Math.abs(upliftPercent ?? 0), 50) * 2}%`}
                transition="width 0.3s ease"
              />
            </Box>
          </Box>
        )}

        {/* Uplift amounts */}
        {hasUpliftData ? (
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.500">
              Potential uplift
            </Text>
            <Text
              fontSize="sm"
              fontWeight="bold"
              color={isPositiveUplift ? 'green.600' : 'red.600'}
            >
              {isPositiveUplift ? '+' : ''}
              {salaryFormatter(upliftDollar ?? 0)} (
              {isPositiveUplift ? '+' : ''}
              {(upliftPercent ?? 0).toFixed(1)}%)
            </Text>
          </HStack>
        ) : (
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.500">
              Potential uplift
            </Text>
            <Text fontSize="xs" color="gray.400" fontStyle="italic">
              Insufficient data
            </Text>
          </HStack>
        )}

        {/* Target salary with skill */}
        {targetSalaryWithSkill !== null && (
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.500">
              {upliftMode === 'percentile'
                ? 'At your percentile'
                : 'Median with skill'}
            </Text>
            <Text fontSize="xs" color="gray.600">
              {salaryFormatter(targetSalaryWithSkill)}
            </Text>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};
